// ShopGuard: dropshipping-checklist per winkel, leverancier en product, met publicatiepoort in de database
// (supabase/migrations/shopguard.sql). Een product met een open blocker kan niet LIVE worden; producten die al
// live staan blijven staan. Wettelijke inhoud telt pas mee als het sjabloon een bron heeft en geverifieerd is.
import { useEffect, useMemo, useState } from "react";
import { supabase } from "../supabase";
import { C, Kop, Tellers, Melding, Venster, Veld, Leeg, Chip, datumNl } from "./gedeeld";
import { ondertekendeUrl } from "./opslag";

const WINKELS = [["store", "Dif3r3nt Store"], ["limited_floor", "Limited Floor"]];
const STAAT = {
  klaar: ["klaar", C.green], nvt: ["n.v.t.", C.muted], open: ["open", C.red],
  verlopen: ["verlopen", C.red], niet_geverifieerd: ["inhoud niet geverifieerd", C.yellow],
};
const ONDERWERP = { winkel: "Winkel", leverancier: "Leverancier", product: "Product" };
const DOMEINEN = ["productveiligheid", "juridisch", "logistiek", "betalingen", "btw", "overig"];
const EVENT = { te_laat: "Te late levering", retour: "Retour", klacht: "Klacht" };
const telt = (staat) => staat === "open" || staat === "verlopen" || staat === "niet_geverifieerd";

export function productKleur(p) {
  return p.blockers > 0 ? C.red : p.waarschuwingen > 0 ? C.yellow : C.green;
}

export default function ShopGuard() {
  const [winkel, setWinkel] = useState("store");
  const [tab, setTab] = useState("producten");
  const [instellingen, setInstellingen] = useState([]);
  const [producten, setProducten] = useState([]);
  const [leveranciers, setLeveranciers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [winkelPunten, setWinkelPunten] = useState([]);
  const [filter, setFilter] = useState("PENDING_APPROVAL");
  const [zoek, setZoek] = useState("");
  const [open, setOpen] = useState(null);
  const [laden, setLaden] = useState(true);
  const [fout, setFout] = useState(null);
  const [toast, setToast] = useState(null);

  function melding(m) { setToast(m); setTimeout(() => setToast(null), 3500); }

  async function laad() {
    setLaden(true);
    const [w, p, l, t, k] = await Promise.all([
      supabase.from("shopguard_winkels").select("*"),
      supabase.rpc("shopguard_producten", { p_winkel: winkel }),
      supabase.rpc("shopguard_leveranciers", { p_winkel: winkel }),
      supabase.from("shopguard_templates").select("*").order("onderwerp_type").order("id"),
      supabase.rpc("shopguard_checklist", { p_winkel: winkel, p_product_id: null, p_supplier_id: null, p_categorie: null }),
    ]);
    const eerste = [w, p, l, t, k].find((x) => x.error);
    setFout(eerste ? (/does not exist|schema cache|Could not find/i.test(eerste.error.message) ? "De database-stap voor ShopGuard is nog niet uitgevoerd (supabase/migrations/shopguard.sql)." : eerste.error.message) : null);
    setInstellingen(w.data || []); setProducten(p.data || []); setLeveranciers(l.data || []); setTemplates(t.data || []);
    setWinkelPunten((k.data || []).filter((x) => x.onderwerp_type === "winkel"));
    setLaden(false);
  }
  useEffect(() => { laad(); /* eslint-disable-next-line */ }, [winkel]);

  const inst = instellingen.find((x) => x.winkel === winkel);
  const zichtbaar = useMemo(() => producten.filter((p) => (filter === "alle" || p.status === filter) && (!zoek || (p.titel || "").toLowerCase().includes(zoek.toLowerCase()))), [producten, filter, zoek]);
  const nietGeverifieerd = templates.filter((t) => t.actief && !t.laatst_geverifieerd_op).length;

  async function zetBarriere(aan) {
    if (!aan && !window.confirm("De publicatiepoort uitzetten? Dan kunnen producten met open blockers weer live.")) return;
    const { error } = await supabase.from("shopguard_winkels").update({ barriere_actief: aan, updated_at: new Date().toISOString() }).eq("winkel", winkel);
    if (error) melding("✗ " + error.message); else { melding(aan ? "✓ Poort aan" : "✓ Poort uit"); laad(); }
  }

  return (
    <div className="fu">
      {toast && <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: toast.startsWith("✗") ? C.redDim : C.greenDim, border: "1px solid " + (toast.startsWith("✗") ? C.red : C.green), color: C.text, padding: "8px 16px", borderRadius: 8, fontSize: 12, zIndex: 999 }}>{toast}</div>}
      <Kop titel="ShopGuard" sub="Checklist voor dropshipping per winkel, leverancier en product. Een product met een open blocker kan niet live; wat al live staat blijft staan."
        rechts={WINKELS.map(([k, l]) => <span key={k} className={"tab" + (winkel === k ? " on" : "")} onClick={() => { setWinkel(k); setOpen(null); }}>{l}</span>)} />
      {fout && <Melding type="fout">{fout}</Melding>}
      {!fout && inst && (
        <Melding type={inst.barriere_actief ? "info" : "let-op"}>
          Publicatiepoort {inst.barriere_actief ? <strong>aan</strong> : <strong>uit</strong>} voor {inst.naam}. {inst.barriere_actief ? "Zet de winkel een product op live terwijl er een blocker open staat, dan weigert de database dat en toont de winkel waarom." : "Producten met open blockers kunnen nu live."}
          {" "}<button className="btn bg" style={{ fontSize: 11, marginLeft: 6 }} onClick={() => zetBarriere(!inst.barriere_actief)}>{inst.barriere_actief ? "Poort uitzetten" : "Poort aanzetten"}</button>
        </Melding>
      )}
      {!fout && nietGeverifieerd > 0 && <Melding type="let-op">{nietGeverifieerd} sjabloon{nietGeverifieerd === 1 ? " is" : "en zijn"} nog niet geverifieerd en tellen daarom als open blocker. Voeg op het tabblad Sjablonen per punt de officiële bron toe en markeer het als geverifieerd.</Melding>}
      <Tellers items={[
        { label: "Wacht op akkoord", waarde: producten.filter((p) => p.status === "PENDING_APPROVAL").length },
        { label: "Klaar om live", waarde: producten.filter((p) => p.status !== "LIVE" && p.blockers === 0).length, kleur: C.green },
        { label: "Live met open punten", waarde: producten.filter((p) => p.status === "LIVE" && p.blockers > 0).length, kleur: C.yellow },
        { label: "Leveranciers < 70", waarde: leveranciers.filter((l) => l.producten > 0 && l.score < 70).length, kleur: C.red },
      ]} />
      <div style={{ display: "flex", gap: 7, marginBottom: 12, flexWrap: "wrap" }}>
        {[["producten", "Producten"], ["leveranciers", "Leveranciers"], ["winkel", "Winkel"], ["sjablonen", "Sjablonen"]].map(([k, l]) => <span key={k} className={"tab" + (tab === k ? " on" : "")} onClick={() => setTab(k)}>{l}</span>)}
      </div>
      {laden && <Leeg tekst="Laden..." />}

      {!laden && tab === "producten" && (
        <div className="card" style={{ padding: 0 }}>
          <div style={{ display: "flex", gap: 7, padding: 10, flexWrap: "wrap", alignItems: "center" }}>
            {[["PENDING_APPROVAL", "Wacht op akkoord"], ["CONCEPT", "Concept"], ["LIVE", "Live"], ["PAUSED", "Gepauzeerd"], ["alle", "Alle"]].map(([k, l]) => <span key={k} className={"tab" + (filter === k ? " on" : "")} style={{ fontSize: 11 }} onClick={() => setFilter(k)}>{l}</span>)}
            <input value={zoek} onChange={(e) => setZoek(e.target.value)} placeholder="Zoek product" style={{ maxWidth: 220, marginLeft: "auto" }} />
          </div>
          {zichtbaar.length === 0 && <Leeg tekst="Geen producten in deze selectie." />}
          {zichtbaar.slice(0, 200).map((p) => (
            <div key={p.id} onClick={() => setOpen({ soort: "product", ...p })} style={{ display: "flex", gap: 10, alignItems: "center", padding: "9px 12px", borderTop: "1px solid " + C.border, cursor: "pointer" }}>
              <span style={{ width: 9, height: 9, borderRadius: "50%", background: productKleur(p), flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.titel}</p>
                <p style={{ fontSize: 11, color: C.muted }}>{p.status} · {p.categorie || "geen categorie"} · {p.leverancier || "geen leverancier"}</p>
              </div>
              <span style={{ fontSize: 11, color: p.blockers ? C.red : C.green, whiteSpace: "nowrap" }}>{p.blockers ? p.blockers + " blocker" + (p.blockers === 1 ? "" : "s") : "geen blockers"}</span>
            </div>
          ))}
          {zichtbaar.length > 200 && <p style={{ fontSize: 11, color: C.dim, padding: 10 }}>Eerste 200 van {zichtbaar.length}; zoek om te verfijnen.</p>}
        </div>
      )}

      {!laden && tab === "leveranciers" && (
        <div className="card" style={{ padding: 0 }}>
          <p style={{ fontSize: 11, color: C.muted, padding: "10px 12px" }}>ShopGuard-score: 100, min 10 per te late levering, 8 per retour en 15 per klacht in de laatste 90 dagen. Alleen te verlagen via een geregistreerde gebeurtenis, nooit met de hand.</p>
          {leveranciers.length === 0 && <Leeg tekst="Geen leveranciers." />}
          {leveranciers.map((l) => (
            <div key={l.id} onClick={() => setOpen({ soort: "leverancier", ...l })} style={{ display: "flex", gap: 10, alignItems: "center", padding: "9px 12px", borderTop: "1px solid " + C.border, cursor: "pointer", opacity: l.producten ? 1 : 0.55 }}>
              <span style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 16, width: 36, color: l.score >= 85 ? C.green : l.score >= 70 ? C.yellow : C.red }}>{l.score}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 13, fontWeight: 600 }}>{l.naam}</p>
                <p style={{ fontSize: 11, color: C.muted }}>{l.land || "land onbekend"} · {l.producten} product{l.producten === 1 ? "" : "en"} live of wachtend · winkelscore {l.betrouwbaarheid_winkel ?? "-"}{l.geschikt === false ? " · ongeschikt in winkel" : ""}</p>
              </div>
              <span style={{ fontSize: 11, color: C.muted, whiteSpace: "nowrap" }}>{l.te_laat} te laat · {l.retour} retour · {l.klacht} klacht</span>
            </div>
          ))}
        </div>
      )}

      {!laden && tab === "winkel" && <PuntenLijst winkel={winkel} punten={winkelPunten} onWijzig={laad} melding={melding} />}
      {!laden && tab === "sjablonen" && <Sjablonen templates={templates} onWijzig={laad} melding={melding} />}

      {open && <DetailVenster winkel={winkel} item={open} onSluit={() => setOpen(null)} onWijzig={laad} melding={melding} />}
    </div>
  );
}

function DetailVenster({ winkel, item, onSluit, onWijzig, melding }) {
  const [punten, setPunten] = useState(null);
  const [ev, setEv] = useState({ type: "te_laat", order_ref: "", notitie: "" });
  const isProduct = item.soort === "product";
  async function laad() {
    const { data, error } = await supabase.rpc("shopguard_checklist", isProduct
      ? { p_winkel: winkel, p_product_id: item.id, p_supplier_id: item.supplier_id, p_categorie: item.categorie }
      : { p_winkel: winkel, p_product_id: null, p_supplier_id: item.id, p_categorie: null });
    if (error) { melding("✗ " + error.message); setPunten([]); return; }
    setPunten(isProduct ? data : data.filter((x) => x.onderwerp_type === "leverancier"));
  }
  useEffect(() => { laad(); /* eslint-disable-next-line */ }, [item.id]);
  async function registreer() {
    const { error } = await supabase.from("shopguard_leverancier_events").insert([{ winkel, supplier_id: item.id, type: ev.type, order_ref: ev.order_ref.trim() || null, notitie: ev.notitie.trim() || null, door: "furgel" }]);
    if (error) { melding("✗ " + error.message); return; }
    melding("✓ Geregistreerd"); setEv({ type: "te_laat", order_ref: "", notitie: "" }); onWijzig();
  }
  return (
    <Venster titel={isProduct ? item.titel : "Leverancier: " + item.naam} onSluit={onSluit} breed={760}>
      {isProduct && <p style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>{item.status} · {item.categorie || "geen categorie"} · {item.leverancier || "geen leverancier gekoppeld"}. Winkel- en leverancierspunten gelden voor alle producten van die winkel of leverancier.</p>}
      {punten === null ? <Leeg tekst="Laden..." /> : <PuntenLijst winkel={winkel} punten={punten} onWijzig={() => { laad(); onWijzig(); }} melding={melding} />}
      {!isProduct && (
        <div className="card" style={{ marginTop: 12 }}>
          <p className="lbl" style={{ marginBottom: 8 }}>Gebeurtenis registreren (verlaagt de score)</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <select value={ev.type} onChange={(e) => setEv((x) => ({ ...x, type: e.target.value }))}>{Object.entries(EVENT).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
            <input value={ev.order_ref} maxLength={100} placeholder="Bestelnummer (optioneel)" onChange={(e) => setEv((x) => ({ ...x, order_ref: e.target.value }))} />
          </div>
          <input value={ev.notitie} maxLength={1000} placeholder="Wat ging er mis?" style={{ marginTop: 8 }} onChange={(e) => setEv((x) => ({ ...x, notitie: e.target.value }))} />
          <button className="btn bp" style={{ marginTop: 8 }} onClick={registreer}>Registreer</button>
        </div>
      )}
    </Venster>
  );
}

function PuntenLijst({ winkel, punten, onWijzig, melding }) {
  if (!punten.length) return <Leeg tekst="Geen checklistpunten van toepassing." />;
  const groepen = ["winkel", "leverancier", "product"].map((t) => [t, punten.filter((p) => p.onderwerp_type === t)]).filter(([, l]) => l.length);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {groepen.map(([t, lijst]) => (
        <div key={t} className="card" style={{ padding: 0 }}>
          <p className="lbl" style={{ padding: "10px 12px 4px" }}>{ONDERWERP[t]} · {lijst.filter((p) => p.ernst === "blocker" && telt(p.staat)).length} open blocker(s)</p>
          {lijst.map((p) => <Punt key={p.template_id + "-" + p.onderwerp_id} winkel={winkel} p={p} onWijzig={onWijzig} melding={melding} />)}
        </div>
      ))}
    </div>
  );
}

function Punt({ winkel, p, onWijzig, melding }) {
  const [bezig, setBezig] = useState(false);
  const [notitie, setNotitie] = useState(p.notitie || "");
  const [s, kleur] = STAAT[p.staat] || [p.staat, C.muted];
  async function bewaar(patch) {
    setBezig(true);
    const rij = { winkel, template_id: p.template_id, onderwerp_type: p.onderwerp_type, onderwerp_id: p.onderwerp_id, status: p.status, bewijs_url: p.bewijs_url, notitie: notitie.trim() || null, ...patch };
    const { error } = await supabase.from("shopguard_items").upsert([rij], { onConflict: "winkel,template_id,onderwerp_type,onderwerp_id" });
    setBezig(false);
    if (error) melding("✗ " + error.message); else onWijzig();
  }
  async function upload(file) {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { melding("✗ Maximaal 15 MB"); return; }
    setBezig(true);
    const ext = (file.name.split(".").pop() || "bin").replace(/[^a-z0-9]/gi, "").slice(0, 8);
    const pad = `shopguard/${winkel}/${p.onderwerp_type}-${p.onderwerp_id}-${p.template_id}-${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from("documents").upload(pad, file, { contentType: file.type || undefined, upsert: false });
    if (error) { setBezig(false); melding("✗ Uploaden mislukt: " + error.message); return; }
    const url = supabase.storage.from("documents").getPublicUrl(pad).data?.publicUrl || null;
    await bewaar({ bewijs_url: url });
    melding("✓ Bewijs toegevoegd");
  }
  async function toonBewijs() { const u = await ondertekendeUrl(p.bewijs_url); window.open(u, "_blank", "noopener,noreferrer"); }
  return (
    <div style={{ borderTop: "1px solid " + C.border, padding: "10px 12px", opacity: bezig ? 0.6 : 1 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <p style={{ fontSize: 13, fontWeight: 600 }}>{p.titel}</p>
          <p style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{p.domein} · {p.bron_url ? <a href={p.bron_url} target="_blank" rel="noopener noreferrer" style={{ color: C.blue }}>bron</a> : "geen bron"}{p.laatst_geverifieerd_op ? " · inhoud geverifieerd " + datumNl(p.laatst_geverifieerd_op) : ""} · geldig {p.geldig_dagen} dagen</p>
          {p.uitleg && <p style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>{p.uitleg}</p>}
          {p.geverifieerd_op && <p style={{ fontSize: 11, color: C.dim, marginTop: 3 }}>Afgevinkt {datumNl(p.geverifieerd_op)} door {p.geverifieerd_door || "?"}</p>}
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <Chip tekst={p.ernst} kleur={p.ernst === "blocker" ? C.red : C.yellow} />
          <Chip tekst={s} kleur={kleur} />
        </div>
      </div>
      <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap", alignItems: "center" }}>
        <select value={p.status} disabled={bezig} style={{ width: "auto" }} onChange={(e) => bewaar({ status: e.target.value })}>
          <option value="open">Open</option><option value="klaar">Klaar</option><option value="nvt">Niet van toepassing</option>
        </select>
        <input value={notitie} maxLength={2000} placeholder="Notitie of link naar bewijs" style={{ flex: 1, minWidth: 160 }} onChange={(e) => setNotitie(e.target.value)} onBlur={() => { if ((notitie.trim() || null) !== (p.notitie || null)) bewaar({}); }} />
        <label className="btn bg" style={{ fontSize: 11, cursor: "pointer" }}>Bewijs uploaden<input type="file" style={{ display: "none" }} disabled={bezig} onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} /></label>
        {p.bewijs_url && <button className="btn bg" style={{ fontSize: 11 }} onClick={toonBewijs}>Bewijs bekijken</button>}
      </div>
      {p.staat === "niet_geverifieerd" && <p style={{ fontSize: 11, color: C.yellow, marginTop: 6 }}>De inhoud van dit punt is nog niet (of niet meer) geverifieerd. Ook afgevinkt blijft het open tot het sjabloon een bron heeft en geverifieerd is.</p>}
    </div>
  );
}

function Sjablonen({ templates, onWijzig, melding }) {
  const [bewerk, setBewerk] = useState(null);
  async function opslaan() {
    const b = bewerk;
    if (b.titel.trim().length < 5) { melding("✗ Titel te kort"); return; }
    if (b.bron_url && !/^https:\/\//.test(b.bron_url.trim())) { melding("✗ De bron moet een https-link zijn"); return; }
    const rij = {
      onderwerp_type: b.onderwerp_type, domein: b.domein, titel: b.titel.trim(), uitleg: b.uitleg.trim() || null, ernst: b.ernst,
      bron_url: b.bron_url.trim() || null, geldig_dagen: Math.max(1, Math.min(3650, Number(b.geldig_dagen) || 365)), actief: b.actief,
      categorie_patronen: b.patronen.trim() ? b.patronen.split(",").map((x) => x.trim()).filter(Boolean).map((x) => (x.includes("%") ? x : "%" + x + "%")) : null,
    };
    // Een andere bron of tekst betekent opnieuw verifiëren.
    const oud = b.id ? templates.find((t) => t.id === b.id) : null;
    if (oud && (oud.bron_url !== rij.bron_url || oud.titel !== rij.titel)) rij.laatst_geverifieerd_op = null;
    const q = b.id ? supabase.from("shopguard_templates").update(rij).eq("id", b.id) : supabase.from("shopguard_templates").insert([rij]);
    const { error } = await q;
    if (error) { melding("✗ " + error.message); return; }
    melding("✓ Opgeslagen"); setBewerk(null); onWijzig();
  }
  async function verifieer(t) {
    if (!t.bron_url) { melding("✗ Voeg eerst een bron toe"); return; }
    if (!window.confirm(`Bevestig dat je de inhoud van "${t.titel}" vandaag hebt gecontroleerd tegen de bron.`)) return;
    const { error } = await supabase.from("shopguard_templates").update({ laatst_geverifieerd_op: new Date().toISOString().slice(0, 10) }).eq("id", t.id);
    if (error) melding("✗ " + error.message); else { melding("✓ Geverifieerd"); onWijzig(); }
  }
  const nieuw = () => setBewerk({ onderwerp_type: "product", domein: "productveiligheid", titel: "", uitleg: "", ernst: "blocker", bron_url: "", geldig_dagen: 365, actief: true, patronen: "" });
  return (
    <div className="card" style={{ padding: 0 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px" }}>
        <p style={{ fontSize: 11, color: C.muted, maxWidth: 560 }}>Sjablonen gelden voor alle winkels. Een sjabloon zonder geverifieerde bron telt als open blocker. Bij een andere bron of tekst moet je opnieuw verifiëren.</p>
        <button className="btn bp" style={{ fontSize: 11 }} onClick={nieuw}>+ Sjabloon</button>
      </div>
      {templates.map((t) => (
        <div key={t.id} style={{ borderTop: "1px solid " + C.border, padding: "10px 12px", opacity: t.actief ? 1 : 0.5 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-start" }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <p style={{ fontSize: 13, fontWeight: 600 }}>{t.titel}</p>
              <p style={{ fontSize: 11, color: C.muted }}>{ONDERWERP[t.onderwerp_type]} · {t.domein} · {t.categorie_patronen ? "categorieën: " + t.categorie_patronen.join(", ") : "alle"} · {t.bron_url ? <a href={t.bron_url} target="_blank" rel="noopener noreferrer" style={{ color: C.blue }}>bron</a> : "geen bron"}</p>
            </div>
            <Chip tekst={t.ernst} kleur={t.ernst === "blocker" ? C.red : C.yellow} />
            <Chip tekst={t.laatst_geverifieerd_op ? "geverifieerd " + datumNl(t.laatst_geverifieerd_op) : "niet geverifieerd"} kleur={t.laatst_geverifieerd_op ? C.green : C.yellow} />
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
            <button className="btn bg" style={{ fontSize: 11 }} onClick={() => setBewerk({ ...t, uitleg: t.uitleg || "", bron_url: t.bron_url || "", patronen: (t.categorie_patronen || []).join(", ") })}>Bewerk</button>
            <button className="btn bg" style={{ fontSize: 11 }} disabled={!t.bron_url} onClick={() => verifieer(t)}>Markeer inhoud als geverifieerd</button>
          </div>
        </div>
      ))}
      {bewerk && (
        <Venster titel={bewerk.id ? "Sjabloon bewerken" : "Nieuw sjabloon"} onSluit={() => setBewerk(null)} breed={600}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            <Veld label="Geldt voor"><select value={bewerk.onderwerp_type} onChange={(e) => setBewerk((b) => ({ ...b, onderwerp_type: e.target.value }))}>{Object.entries(ONDERWERP).map(([k, l]) => <option key={k} value={k.toString()}>{l}</option>)}</select></Veld>
            <Veld label="Domein"><select value={bewerk.domein} onChange={(e) => setBewerk((b) => ({ ...b, domein: e.target.value }))}>{DOMEINEN.map((d) => <option key={d} value={d}>{d}</option>)}</select></Veld>
            <Veld label="Ernst"><select value={bewerk.ernst} onChange={(e) => setBewerk((b) => ({ ...b, ernst: e.target.value }))}><option value="blocker">Blocker</option><option value="waarschuwing">Waarschuwing</option></select></Veld>
          </div>
          <Veld label="Controlevraag"><input value={bewerk.titel} maxLength={300} onChange={(e) => setBewerk((b) => ({ ...b, titel: e.target.value }))} /></Veld>
          <Veld label="Uitleg (optioneel)"><textarea rows={2} value={bewerk.uitleg} onChange={(e) => setBewerk((b) => ({ ...b, uitleg: e.target.value }))} /></Veld>
          <Veld label="Officiële bron (https)"><input value={bewerk.bron_url} placeholder="https://..." onChange={(e) => setBewerk((b) => ({ ...b, bron_url: e.target.value }))} /></Veld>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 8 }}>
            <Veld label="Alleen voor categorieën (komma gescheiden, leeg = alle)" hulp="Bijv. elektronica, speelgoed. Alleen voor productpunten."><input value={bewerk.patronen} onChange={(e) => setBewerk((b) => ({ ...b, patronen: e.target.value }))} /></Veld>
            <Veld label="Geldig (dagen)"><input type="number" min="1" max="3650" value={bewerk.geldig_dagen} onChange={(e) => setBewerk((b) => ({ ...b, geldig_dagen: e.target.value }))} /></Veld>
          </div>
          <label style={{ display: "flex", gap: 7, alignItems: "center", fontSize: 12, marginBottom: 12 }}><input type="checkbox" style={{ width: "auto" }} checked={!!bewerk.actief} onChange={(e) => setBewerk((b) => ({ ...b, actief: e.target.checked }))} /> Actief</label>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn bg" onClick={() => setBewerk(null)}>Annuleer</button>
            <button className="btn bp" onClick={opslaan}>Opslaan</button>
          </div>
        </Venster>
      )}
    </div>
  );
}
